import type { Listing } from '@thevillages/schemas';

import { isPlatformServer } from '@angular/common';
import { HttpClient, httpResource } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  linkedSignal,
  PLATFORM_ID,
  signal,
  untracked,
} from '@angular/core';

import { Environment } from '../../../environments/environment';
import { FilteringService } from '../filtering';
import { StateService } from '../state/state';
import { AllListings } from './all-listings';
import { FilteredListings } from './filtered-listings';
import { NoResultsReason } from './no-results-reason';
import { SelectedListing } from './selected-listing';
import { SetSearchOptions } from './set-search-options';
import { SidebarListings } from './sidebar-listings';
import { SourceListings } from './source';

@Injectable({
  providedIn: 'root',
})
export class ListingsService {
  protected readonly filteringService = inject(FilteringService);
  protected readonly httpClient = inject(HttpClient);
  protected readonly stateService = inject(StateService);

  private readonly _platformId = inject(PLATFORM_ID);

  /*
  This gets all the listings from the API so they can be prerendered with the SSR. The filteredListings
  computed will then filter this list on the client side. This allows for very fast filtering without
  additional API calls, at the cost of increased memory usage on the client.
  */
  public readonly allListings = computed<Listing[]>(() => AllListings.call(this));
  public readonly chunk = signal(12);
  public readonly filteredListings = computed(() => FilteredListings.call(this));
  public readonly noResultsReason = computed(() => NoResultsReason.call(this));
  /*
  These are the visible listings (map and sidebar).
  */
  // readonly mapListings = computed(() => MapListings.call(this));
  /*
  These are the visible listings (map and sidebar).
  */
  public readonly savedListingIds = signal<string[]>([]);
  public readonly savedListingsAvailableCount = computed(() => {
    const _allIds = this.allListings();
    return (
      this.filteringService
        .userSavedListingIds()
        .filter((l) => l.itemId !== '' && _allIds.some((a) => a.id === l.itemId)).length || 0
    );
  });
  public readonly selectedListing = linkedSignal<Listing | undefined>(() =>
    SelectedListing.call(this),
  );
  /*
  These are the visible listings in the sidebar (paginated).
  This also needs to filter out listings that are outside of the map extent.
  */
  public readonly sidebarListings = computed(() => SidebarListings.call(this));

  protected readonly isServer = signal(isPlatformServer(this._platformId));
  protected readonly source = httpResource<Listing[]>(() => ({
    ...SourceListings(),
    headers: {
      Authorization: this.stateService.userToken()
        ? `Bearer ${this.stateService.userToken()}`
        : Environment.basicToken,
    },
  }));

  constructor() {
    effect(() => {
      if (this.allListings().length) {
        untracked(() => {
          this.stateService.allListingIds.set(
            this.allListings().map((listing) => ({
              id: listing.id || '',
              modelId: listing.model?.id,
            })),
          );

          this.stateService.initialListingsLoaded.set(true);
        });
      }
    });

    effect(() => {
      if (!this.filteringService.searchOptionProps().length) {
        return;
      }

      SetSearchOptions.call(this);
    });

    // Displays a single listing if the filters result in only one listing. This is especially useful for users coming from a
    // saved search link that has very specific filters.
    // effect(() => {
    //   if (this.filteredListings().length === 1) {
    //     this.stateService.selectedListingId.set(this.filteredListings()![0].id ?? '');
    //   }
    // });
  }

  public getListingById(id: string): Listing | undefined {
    return this.allListings().find((listing) => listing.id === id);
  }
}
